/**
 * 개발요청 — 「어느 메뉴의 요청인가」 고르기 (v2.274.0 · 2026-10-18 사용자 「처리범위는 전 메뉴」)
 *
 * ⭐메뉴 목록의 원본은 `lib/menu.ts` 한 곳이다 — 여기서 따로 적지 않고 그 표에서 뽑는다
 *   (개발 계획 v2.180.0 과 같은 판단 · 서버는 이름표를 모르고 열쇠 글자 모양만 본다).
 *   열쇠 = 화면 주소('/' 대시보드 · '/sales' …) · 'common'(여러 메뉴·전체 공통) · 'new-menu'(새 메뉴 제안).
 *   ⛔서버 `DEV_REQUEST_MENU_KEY_RE` · DB CHECK(seq_0013 dev_request_menu_key_check)와 같은 모양.
 */

import { MENU_GROUPS, pathOnly, visibleItems } from './menu';
import type { AuthUser } from './types';

export const DEV_MENU_COMMON = 'common';
export const DEV_MENU_NEW = 'new-menu';

/** 메뉴에 없는 화면이지만 오른쪽 위 단추가 보이는 곳 */
const EXTRA_PAGES: Record<string, string> = { '/mypage': '👤 마이페이지' };

const SPECIAL: Record<string, string> = {
  [DEV_MENU_COMMON]: '🌐 여러 메뉴·전체 공통',
  [DEV_MENU_NEW]: '🆕 새 메뉴 제안',
};

export interface DevMenuOption { key: string; label: string; group: string }

/** 고르기 목록 — 그 사람에게 보이는 메뉴(사이드바와 같은 거르기) + 마이페이지 + 공통·새 메뉴 */
export function devMenuOptions(user: AuthUser | null): DevMenuOption[] {
  const out: DevMenuOption[] = [];
  for (const g of MENU_GROUPS) {
    for (const it of visibleItems(g.items, user)) out.push({ key: pathOnly(it.path), label: it.label, group: g.heading });
  }
  for (const [key, label] of Object.entries(EXTRA_PAGES)) out.push({ key, label, group: '그 밖' });
  for (const [key, label] of Object.entries(SPECIAL)) out.push({ key, label, group: '그 밖' });
  return out;
}

/** 열쇠 → 이름표. 메뉴가 없어졌거나 내게 안 보이는 메뉴여도 이름은 나온다(전체 표에서 찾는다) */
export function devMenuLabel(key: string): string {
  if (SPECIAL[key]) return SPECIAL[key]!;
  if (EXTRA_PAGES[key]) return EXTRA_PAGES[key]!;
  for (const g of MENU_GROUPS) {
    for (const it of g.items) if (pathOnly(it.path) === key) return it.label;
  }
  return key;
}

/**
 * 지금 화면 → 기본으로 고를 열쇠. 메뉴 주소와 같으면 그 메뉴 · 메뉴 주소 아래(`/stock/…`)면 그 메뉴 ·
 * 마이페이지 · 그 밖은 「여러 메뉴·전체 공통」.
 */
export function devMenuKeyForPath(pathname: string): string {
  const p = pathOnly(pathname).replace(/\/+$/, '') || '/';
  let best = '';
  for (const g of MENU_GROUPS) {
    for (const it of g.items) {
      const k = pathOnly(it.path);
      if (k === p) return k;
      if (k !== '/' && p.startsWith(`${k}/`) && k.length > best.length) best = k;
    }
  }
  if (best) return best;
  if (EXTRA_PAGES[p]) return p;
  return DEV_MENU_COMMON;
}
